import { EnvPill } from "@/components/iris";
import { RealtimeSection } from "@/features/presence/realtime-section";
import { SessionSection } from "@/features/presence/session-section";
import { TablesSection } from "@/features/presence/tables-section";
import { TrendsSection } from "@/features/presence/trends-section";
import { UsageSection } from "@/features/presence/usage-section";
import { usePresenceRealtime, usePresenceSummary, usePresenceUsage } from "@/hooks/use-presence";
import type { Env } from "@/lib/types";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_authed/presence")({
  component: PresencePage,
});

/** Skeleton placeholder row mirroring the analytics loading state. */
function SkeletonRow({ count }: { count: number }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${count}, 1fr)`, gap: 16 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders
          key={i}
          className="iris-card"
          style={{ height: 128, opacity: 0.5 }}
        />
      ))}
    </div>
  );
}

function EmptyCard({ message }: { message: string }) {
  return (
    <div
      className="iris-card"
      style={{
        padding: 48,
        textAlign: "center",
        color: "var(--iris-fg-4)",
        fontSize: "var(--iris-t-body)",
      }}
    >
      {message}
    </div>
  );
}

function PresencePage() {
  // IR-1148: scope the dashboard to the selected env — dev/prod run independent
  // DBs, so presence diverges. Default prod (ops convention).
  const [env, setEnv] = useState<Env>("prod");

  const realtime = usePresenceRealtime(env);
  const usage = usePresenceUsage(env);
  const summary = usePresenceSummary(env);

  return (
    <div className="iris-page">
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 className="iris-page__title" style={{ fontSize: 32, marginBottom: 4 }}>
            Presence
          </h1>
          <span className="iris-meta">Live user presence, usage, and session activity</span>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <EnvPill value={env} onChange={setEnv} />
        </div>
      </div>

      {/* Live presence */}
      {realtime.isLoading ? (
        <SkeletonRow count={6} />
      ) : realtime.error || !realtime.data ? (
        <EmptyCard message="No live presence data available." />
      ) : (
        <RealtimeSection data={realtime.data.data} env={env} />
      )}

      {/* Active users */}
      {usage.isLoading ? (
        <div style={{ marginTop: 32 }}>
          <SkeletonRow count={3} />
        </div>
      ) : usage.error || !usage.data ? (
        <div style={{ marginTop: 32 }}>
          <EmptyCard message="No usage data available." />
        </div>
      ) : (
        <UsageSection data={usage.data.data} env={env} />
      )}

      {/* Session quality */}
      {summary.isLoading ? (
        <div style={{ marginTop: 32 }}>
          <SkeletonRow count={5} />
        </div>
      ) : summary.error || !summary.data ? (
        <div style={{ marginTop: 32 }}>
          <EmptyCard message="No session summary available." />
        </div>
      ) : (
        <SessionSection data={summary.data.data} env={env} />
      )}

      {/* Trends + tables manage their own loading/empty states */}
      <TrendsSection env={env} />
      <TablesSection env={env} />
    </div>
  );
}
